<script lang="ts" setup>
import { computed, useAttrs, type Component } from 'vue';
import { Primitive, type PrimitiveProps } from 'radix-vue';
import { buttonVariants } from '.';
import { cn } from '../../../lib/utils';
import { buttonRecipe } from './buttonRecipe';
import { logger } from '@pkg/logger';
import type { PantryButtonConfig } from './types.ts';
import { PhHeart, PhArrowRight, PhX } from '@phosphor-icons/vue';
import { IconUi } from '../icon';
import { LoadingIcon } from '../loading-icon';

export interface ButtonProps extends PrimitiveProps {
  variant?: NonNullable<Parameters<typeof buttonVariants>[0]>['variant'];
  recipeVariant?: PantryButtonConfig['recipeVariant'];
  noTextTransform?: boolean;
  as?: string;
  isActive?: boolean;
  config?: PantryButtonConfig;
  disabled?: boolean;
}

const props = withDefaults(defineProps<ButtonProps>(), {
  as: 'button',
  noTextTransform: false,
  state: 'Idle',
});

const attrs = useAttrs();

function isDisabledAttr(value: unknown): boolean {
  return value === true || value === '' || value === 'true';
}

const effectiveDisabled = computed(() => {
  const fromProps = props.disabled as unknown;
  if (isDisabledAttr(fromProps)) {
    return true;
  }
  if (fromProps === false || fromProps === 'false') {
    return false;
  }
  return isDisabledAttr(attrs.disabled);
});

const icons = {
  heart: PhHeart,
  ArrowRight: PhArrowRight,
  Close: PhX,
};

const buttonRecipeClass = computed(() => {
  const fallbackVariant =
    props.variant === 'text' || props.variant === 'link' ? 'Text' : undefined;
  const variantFromConfig = fallbackVariant ?? props.config?.recipeVariant;
  return buttonRecipe({
    variant: variantFromConfig,
    size: props.config?.size ?? 'large',
    disabled: effectiveDisabled.value,
  });
});

const shouldUseRecipeClass = computed(() =>
  props.variant !== 'dropdown' && props.variant !== 'icon' && props.variant !== 'link',
);

const trailingIconComponent = computed(() => {
  /*if (props.variant === 'text' || props.variant === 'link') {
    return icons.ArrowRight;
  }*/
  return undefined; // props.config?.trailingIcon
});

if (!props.as && props.variant === 'link') {
  logger.warn('[UiButton.vue] Looks like a button is being used with variant="link", but it is defaulting to a &lt;button> tag. Button tags cannot be inside of a tags. If this button is inside a link, use :as="span". To silence this warning, explicitly set the as prop on UiButton to "button"');
}

</script>

<template>
  <Primitive
    :as="props.as"
    :as-child="props.asChild"
    :disabled="effectiveDisabled"
    :class="[shouldUseRecipeClass ? buttonRecipeClass : null, 'c-button-group-link']"
    :type="props.as === 'button' ? $attrs.type || 'button' : undefined"
    class="relative"
  >
    <span v-if="props.config?.loading" class="absolute inset-0 flex items-center justify-center">
      <LoadingIcon :color="props.config?.loadingIconColor ?? 'currentColor'" :size="props.config?.size ?? 'medium'" />
    </span>
    <component
      v-if="props.config?.leadingIcon && !props.config?.loading"
      :is="props.config?.leadingIcon"
      v-bind="props.config?.leadingIconProps"
      aria-hidden="true"
    />
    <span class="button-text" :class="cn(props.config?.loading ? 'opacity-0' : 'opacity-100')">
      <slot />
    </span>
    <IconUi :trailing-icon="trailingIconComponent" :loading="props.config?.loading" />
    <span v-if="props.variant === 'dropdown'" class="relative h-24">
      <ChevronDownIcon :class="cn('absolute h-24 w-24', { 'rotate-180': props.isActive })" />
    </span>
  </Primitive>
</template>
